<template>
    <b-modal
            :id="modalId"
            :visible="show"
            @hidden="hideEvents"
            title="Add Client Action"
            :no-close-on-backdrop="true"
    >
      <vue-element-loading :active="loading" :is-full-screen="false"/>

      <form-wizard
          shape="tab"
          stepSize="sm"
          title=""
          subtitle=""
          color="#4E738A"
          ref="wizard"
      >
        <tab-content
            v-for="tab in tabs"
            :key="tab.title"
            :title="tab.title"
            :before-change="() => validateTab(tab.title)">
          <r-form
            :disabled="false"
            :validators="tab.validators"
            v-model="tab.form"
            :showHeader="false"
            :showFooter="false"
            :ref="tab.title"/>
        </tab-content>

        <tab-content title="Photos">
          <file-drop
              fileUrl="/"
              ref="files"
              hide-errors
          ></file-drop>
        </tab-content>


        <template slot="footer" slot-scope="props">
          <div></div>
        </template>

      </form-wizard>

      <template slot="modal-footer">
        <b-button
            v-if="canGoBack"
            :disabled="loading"
            @click="backTab"
            variant="outline-secondary">
          Back
        </b-button>
        <div v-else></div>

        <b-button
            v-if="!isLastTab"
            @click="nextTab"
            variant="primary"
            :disabled="loading"
        >
        Next
        </b-button>

        <b-button
            @click="addClientAction"
            class="btn-arrow"
            variant="primary"
            v-if="isLastTab"
            :disabled="loading"
        >
          Create Client Action
          <font-awesome-icon
              :icon="['far', 'arrow-right']">
          </font-awesome-icon>
        </b-button>
      </template>

    </b-modal>
</template>

<script>
import ModalMixin from '@/components/Modal/ModalMixin';
import FileDrop from '@/components/Form/FileDrop';
import {
  actionDetailForm, actionDetailsFormValidators,
  actionForm, actionFormValidators,
  photosForm,
  propertyForm, propertyFormValidators,
} from '@/services/Action/ClientActionForm';
import { forEach } from 'lodash';
import ClientAction from '@/services/Action/ClientAction';
import Property from '@/services/Property/Property';
import ClientActionCategory from '../../../services/Action/ClientActionCategory';
import ClientActionPriority from '../../../services/Action/ClientActionPriority';
import AssessmentType from '@/services/Assessment/AssessmentType';
import PropertyAssessmentType from '../../../services/Property/PropertyAssessmentType';
import riskhub from '@/services/Riskhub/Riskhub';
import dayjs from 'dayjs';


export default {
  name: 'Add-Client-Action-Modal',
  components: {
    FileDrop,
  },
  props: {
    property: {
      default: null,
    },
    linkedAction: {
      default: null,
    },
    complianceType: {
      default: null,
    },
    assessmentType: {
      default: null,
    },
    enablePropertySelect: {
      default: false,
    },
  },
  mixins: [ModalMixin],
  data() {
    return {
      modalId: 'addClientActionModal',
      loading: false,

      propertyTab: {
        form: propertyForm(),
        validators: propertyFormValidators,
        title: 'Property',
      },
      actionTab: {
        form: actionForm(),
        validators: actionFormValidators,
        title: 'Action',
      },
      actionDetailTab: {
        form: actionDetailForm(),
        validators: actionDetailsFormValidators,
        title: 'Detail',
      },
      photosTab: {
        form: photosForm(),
        title: 'Photos',
      },
      uploadFiles: false,
      noPropertiesFound: false,
      selectedProperty: null,
      selectedAction: null,
      selectedCompliance: null,
      categoryData: null,
      complianceTypes: [],
      assessmentTypes: [],
      hasProgressed: false,
    };
  },
  computed: {
    tabs() {
      return [
        this.propertyTab,
        this.actionTab,
        this.actionDetailTab,
      ];
    },
    isLastTab() {
      if (!this.hasProgressed) {
        return false;
      }

      if (!this.$refs.wizard) {
        return false;
      }

      return this.$refs.wizard.isLastStep;
    },
    canGoBack() {
      if (!this.$refs.wizard) {
        return false;
      }

      return this.hasProgressed && this.$refs.wizard.activeTabIndex !== 0;
    },
    complianceTypeId() {
      if (this.propertyTab.form.linkedActionId.value) {
        return this.propertyTab.form.linkedActionId.value.assessmentTypeId;
      }

      if (this.propertyTab.form.complianceTypeId.value) {
        return this.propertyTab.form.complianceTypeId.value.assessmentTypeId;
      }

      return null;
    },
    assessmentTypeId() {
      if (this.propertyTab.form.linkedActionId.value) {
        return this.propertyTab.form.linkedActionId.value.assessmentTypeId;
      }

      if (this.propertyTab.form.assessmentTypeId.value) {
        return this.propertyTab.form.assessmentTypeId.value.assessmentTypeId;
      }

      return null;
    },
    originId() {
      if (this.propertyTab.form.linkedActionId.value) {
        return ClientActionCategory.ORIGIN_RISK_ASSESSMENT_ID();
      }

      if (this.propertyTab.form.complianceTypeId.value) {
        return ClientActionCategory.ORIGIN_PROPERTY_ID();
      }

      return null;
    },
  },
  watch: {
    property: {
      async handler(newSelected) {
        if (!newSelected) {
          return;
        }
        this.propertyTab.form.propertyId.value = newSelected;
      },
      immediate: true,
    },
    'propertyTab.form.propertyId.value': {
      async handler(newSelected) {
        this.resetComplianceTypeSelect();
        this.resetAssessmentTypeSelect();
        if (!newSelected) {
          this.resetActionFields();
          this.resetPrioritySelect();
          return;
        }
        this.searchActions(newSelected);
        this.loadComplianceTypes();
      },
      immediate: true,
    },
    'propertyTab.form.propertyId.options': {
      async handler(newSelected) {
        if (this.property) {
          this.propertyTab.form.propertyId.value = newSelected.find(({ propertyId }) =>
            this.property.propertyId === propertyId);
        }
      },
      immediate: true,
    },
    'propertyTab.form.linkedActionId.value': {
      async handler(newSelected) {
        this.selectedAction = newSelected;
        this.resetComplianceTypeSelect();
        this.resetAssessmentTypeSelect();
        if (!newSelected) {
          this.loadComplianceTypes();
          this.resetPrioritySelect();
          return;
        }
        this.populateActionFields(newSelected);
        this.loadPriorities(ClientActionCategory.ORIGIN_RISK_ASSESSMENT_ID());
        this.loadInitialCategory();
      },
      immediate: true,
    },
    'propertyTab.form.complianceTypeId.value': {
      async handler(newSelected) {
        if (newSelected) {
          this.selectedCompliance = newSelected;
          await this.loadAssessmentTypes();
          await this.loadPriorities(ClientActionCategory.ORIGIN_PROPERTY_ID());
          await this.loadInitialCategory();
        }
      },
      immediate: true,
    },
    'actionDetailTab.form.rate.value': {
      handler() {
        this.recalculateMinMaxCosts();
      },
    },
    'actionDetailTab.form.minQuantity.value': {
      handler() {
        this.recalculateMinMaxCosts();
      },
    },
    'actionDetailTab.form.maxQuantity.value': {
      handler() {
        this.recalculateMinMaxCosts();
      },
    },

  },
  mounted() {
    this.propertyTab.form.propertyId.disabled = !this.enablePropertySelect;

    this.setWatchersOnCategories();
  },

  methods: {
    validateTab(ref) {
      return this.$refs[ref][0].isValidated();
    },
    nextTab() {
      this.$refs.wizard.nextTab();
      this.hasProgressed = true;
    },
    backTab() {
      this.$refs.wizard.prevTab();
    },
    recalculateMinMaxCosts() {
      const { form } = this.actionDetailTab;

      const rate = form.rate.value;
      const minQuantity = form.minQuantity.value;
      const maxQuantity = form.maxQuantity.value;

      const minCost = rate * minQuantity;
      const maxCost = rate * maxQuantity;

      form.minCost.value = minCost.toFixed(2);
      form.maxCost.value = maxCost.toFixed(2);
    },

    async loadComplianceTypes() {
      if (!this.selectedProperty) {
        return;
      }

      const { data } = await PropertyAssessmentType
          .list(this.selectedProperty.propertyId, { enabled: 1 });

      this.complianceTypes = data.data.map(({ assessment_type }) => assessment_type);
      this.propertyTab.form.complianceTypeId.options = this.complianceTypes;
      this.propertyTab.form.complianceTypeId.disabled = false;
    },

    async loadAssessmentTypes() {
      if (!this.selectedCompliance) {
        return;
      }

      const { data } = await AssessmentType
        .list({ parentTypeId: this.selectedCompliance.assessmentTypeId });

      this.assessmentTypes = data.data;
      this.propertyTab.form.assessmentTypeId.options = this.assessmentTypes;
      this.propertyTab.form.assessmentTypeId.disabled = false;
    },

    /**
     * Setup custom watchers for the select dropdowns
     * */
    setWatchersOnCategories() {
      for (let i = 0; i < ClientActionCategory.MAX_DEPTH(); i++) {
        const key = `clientActionCategoryId-${i}`;
        const childKey = `clientActionCategoryId-${i + 1}`;

        const { form } = this.actionTab;

        this.$watch(() => form[key].value, async (data) => {
          if (!form[childKey]) {
            return;
          }

          // Reset, will trigger this same watch event for children
          if (!data) {
            form[childKey].value = null;
            return;
          }

          const { clientActionCategoryId } = data;

          if (!clientActionCategoryId) {
            return;
          }

          form[childKey].options = await this.loadCategories(
            this.originId,
            this.complianceTypeId,
            clientActionCategoryId,
          );
        });
      }
    },

    async loadInitialCategory() {
      const formElement = this.actionTab.form['clientActionCategoryId-0'];
      formElement.value = null;
      formElement.options =
          await this.loadCategories(this.originId, this.complianceTypeId);
    },

    /**
     * load the categories associated with the property i.e. where there isn't an associated riskhub action
     * @returns {Promise<void>}
     */
    async loadCategories(originId, complianceTypeId, parentCategoryId = 0) {
      // retrieve all the category data if we don't already have it
      const { data } = await ClientActionCategory.list(originId, complianceTypeId, parentCategoryId);
      this.categoryData = data.data;

      return data.data;
    },
    /**
     * load the categories associated with the property i.e. where there isn't an associated riskhub action
     * @returns {Promise<void>}
     */
    async loadPriorities(originId) {
      // retrieve all the category data if we don't already have it
      const { data } = await ClientActionPriority.list(originId);
      const { form } = this.actionTab;
      form.clientActionPriorityId.options = data.data;
      form.clientActionPriorityId.disabled = false;
    },
    formatAddress(data) {
      const validatedStreetNumber = data.streetNumber || '';
      const validatedStreet = data.street || '';

      return `${data.uprn} - ${[
        data.numberRange,
        data.buildingName,
        `${validatedStreetNumber} ${validatedStreet}`,
        data.town,
        data.postcode,
      ].filter(Boolean).join(', ')}`;
    },
    async searchActions(property) {
      this.selectedProperty = property;

      // Reset fields
      this.resetActionFields();

      const { form } = this.propertyTab;

      const { data } = await Property.getActions(this.selectedProperty.propertyId);

      const results = data.data.map(item => ({ ...item, label: `${item.clientActionId} - ${item.recommendation}` }));

      form.linkedActionId.options = results;

      if (this.linkedAction) {
        const selected = results.find(row => row.clientActionId === this.linkedAction.clientActionId);
        this.populateActionFields(selected);
        form.linkedActionId.disabled = true;
      } else {
        form.linkedActionId.disabled = false;
      }
    },

    resetActionFields() {
      const { form } = this.propertyTab;

      form.linkedActionId.value = null;
      form.linkedActionPriority.value = '';
      form.linkedActionDueDate.value = '';
      form.linkedActionStatus.value = '';
      form.linkedActionId.options = [];
      form.linkedActionId.disabled = true;
    },

    populateActionFields(action) {
      const { form } = this.propertyTab;

      form.linkedActionId.value = action;
      form.linkedActionPriority.value = action.priority;
      form.linkedActionDueDate.value = riskhub.formatDate(action.dueDate);
      form.linkedActionStatus.value = this.uppercaseFirst(action.status);
    },

    resetComplianceTypeSelect() {
      const { form } = this.propertyTab;

      form.complianceTypeId.disabled = true;
      form.complianceTypeId.value = null;
      form.complianceTypeId.options = [];
    },

    resetAssessmentTypeSelect() {
      const { form } = this.propertyTab;

      form.assessmentTypeId.disabled = true;
      form.assessmentTypeId.value = null;
      form.assessmentTypeId.options = [];
    },

    resetPrioritySelect() {
      const { form } = this.actionTab;

      form.clientActionPriorityId.value = null;
      form.clientActionPriorityId.options = [];
      form.clientActionPriorityId.disabled = true;
    },

    uppercaseFirst(string) {
      if (!string) {
        return '';
      }

      return string.charAt(0)
        .toUpperCase() + string.slice(1);
    },

    async save() {
      if (!this.$refs.addClientActionForm.isValidated()) {
        return;
      }

      this.loading = true;
    },

    getParams() {
      const forms = [
        this.actionTab.form,
        this.actionDetailTab.form,
      ];

      const linkedActionId = (this.propertyTab.form.linkedActionId.value) ?
        this.propertyTab.form.linkedActionId.value.clientActionId : null;

      const params = new FormData();

      params.set('linkedActionId', linkedActionId);
      params.set('propertyId', this.selectedProperty.propertyId);
      const dueDate = this.actionTab.form.dueDate.value;

      if (dueDate) {
        params.set('dueDate', dayjs(dueDate).format('YYYY-MM-DD'));
      }

      const makeParams = (form) => {
        forEach(form, (object, field) => {
          if (!object.disabled && !params.has(field)) {
            params.append(field, object.value);
          }
        });
      };

      forms.forEach((form) => {
        makeParams(form);
      });

      // Set the select value
      const setSelectValue = (fieldName, form, valueFieldName = null, paramName = null, append = false) => {
        const { value } = form[fieldName];

        const formFieldName = valueFieldName || fieldName;
        let paramValue = value;
        if (value && value[formFieldName]) {
          paramValue = value[formFieldName];
        }

        // Set a param field name
        const paramFieldName = paramName || fieldName;

        if (append) {
          // This doesn't help anyone having a null array value
          if (paramValue === 'null' || paramValue == null) {
            return;
          }

          params.append(paramFieldName, paramValue);
          return;
        }

        params.set(paramFieldName, paramValue);
      };

      this.selectedAction
        ? params.set('complianceTypeId', this.selectedAction.complianceTypeId)
        : setSelectValue(
            'complianceTypeId',
              this.propertyTab.form,
              'assessmentTypeId',
          );

      this.selectedAction
          ? params.set('assessmentTypeId', this.selectedAction.assessmentTypeId)
          : setSelectValue('assessmentTypeId', this.propertyTab.form);

      const selectValues = [
        {
          fields: [
            'clientActionPriorityId',
            'clientActionTypeId',
          ],
          form: this.actionTab.form,
        },
      ];

      selectValues.forEach(({ fields, form }) =>
        fields.forEach(field => setSelectValue(field, form)));

      for (let i = 0; i < ClientActionCategory.MAX_DEPTH(); i += 1) {
        const key = `clientActionCategoryId-${i}`;
        params.delete(key);
        setSelectValue(
          key,
          this.actionTab.form,
            'clientActionCategoryId',
          'clientActionCategoryId[]',
          true,
        );
      }


      this.$refs.files.getFiles().forEach(file => params.append('file[]', file));

      params.forEach((val, key) => {
        // Remove null / empty values
        if (key === 'clientActionCategoryId[]') {
          return true;
        }

        if (val === 'null' || val.length === 0) {
          params.delete(key);
        }
      });

      return params;
    },
    async addClientAction() {
      this.loading = true;

      try {
        const params = this.getParams();

        await ClientAction.add(params);
        this.complete();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    complete() {
      const title = 'Client action added';
      const text = 'New client action created';
      const type = 'success';

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });
      this.$store.dispatch('refreshActions');
      this.$emit('refresh-all');
      this.hideModal();
    },
  },
};
</script>

<style lang="scss">
    #addClientActionModal {
        .multiselect__tags {
            padding-right: 50px;
        }
        .multiselect__content-wrapper {
            .multiselect__element {
                span {
                    white-space: normal;
                }
            }
        }
    }


</style>
